import type { ReactNode } from 'react'
import { cn } from '#app/utils/misc'
import { useSceneClock } from '#app/utils/site/scene-clock'
import {
	type AvatarTone,
	MORPH_COPY,
	MORPH_DEVICES,
	MORPH_FIRST_STEP,
	MORPH_KEYFRAMES,
	MORPH_LOOP_MS,
	MORPH_STEPS,
	type MorphScreenState,
	type MorphStepId,
	deviceForStep,
	progressFor,
	screenStateFor,
	stepLabel,
} from '#app/utils/site/scenes/device-morph'
import { Wordmark } from '../../Wordmark'
import type { SceneProps } from './types'

/**
 * "Apply From Any Device": one dark frame morphs phone > tablet > laptop
 * while the five application steps run inside it. The schedule, the device
 * per step and the demo copy come from app/utils/site/scenes/device-morph.ts;
 * the morph itself is CSS transitions keyed off `data-d` (see
 * app/styles/site/scene-device-morph.css). Everything inside the screen is
 * sized in em, so one markup scales to all three devices.
 *
 * The whole tile is decorative art: FeatureRow's section carries the
 * accessible description, so the stage and the pills are aria-hidden.
 */
export function DeviceMorphScene({ active }: SceneProps) {
	const { phase: step } = useSceneClock({
		active,
		keyframes: MORPH_KEYFRAMES,
		loopMs: MORPH_LOOP_MS,
		finalPhase: MORPH_FIRST_STEP,
	})
	const device = deviceForStep(step)
	const progress = progressFor(step)

	return (
		<div
			data-scene="device-morph"
			className="tt-scene-device-morph flex w-full flex-col items-center gap-[22px]"
			aria-hidden="true"
		>
			<div className="tt-scene-device-morph-stage -mx-1.5 flex w-[calc(100%+12px)] items-center justify-center md:-mx-4 md:w-[calc(100%+32px)]">
				<div className="tt-scene-device-morph-frame" data-d={device}>
					<div className="tt-scene-device-morph-app relative flex h-full w-full flex-col overflow-hidden bg-white text-tt-ink">
						<div className="tt-scene-device-morph-bar flex items-center justify-between px-[1.6em] pt-[1.6em]">
							<Wordmark title={null} className="h-[1.25em] text-tt-ink" />
							<span className="tt-tabular whitespace-nowrap text-[1em] text-tt-muted">
								{stepLabel(step)}
							</span>
						</div>
						<div className="grid grid-cols-5 gap-[.4em] px-[1.6em] pt-[1em]">
							{progress.map((done, index) => (
								<i
									key={index}
									data-done={done || undefined}
									className="tt-scene-device-morph-seg relative block h-[.35em] overflow-hidden rounded-[1em] bg-tt-inset"
								/>
							))}
						</div>
						<div className="relative flex-1">
							{MORPH_STEPS.map(({ id }) => (
								<Screen key={id} id={id} state={screenStateFor(step, id)} />
							))}
						</div>
					</div>
				</div>
			</div>
			<div className="flex gap-1.5 rounded-full bg-white p-[5px] shadow-tt-soft">
				{MORPH_DEVICES.map((name) => (
					<span
						key={name}
						data-on={device === name || undefined}
						className="tt-scene-device-morph-tab rounded-full px-4 py-2 text-[13px] font-semibold text-tt-muted"
					>
						{MORPH_COPY.tabs[name]}
					</span>
				))}
			</div>
		</div>
	)
}

/* ------------------------------------------------------------------------ */
/* The five screens                                                          */
/* ------------------------------------------------------------------------ */

function Screen({ id, state }: { id: MorphStepId; state: MorphScreenState }) {
	return (
		<div
			data-step={id}
			data-state={state}
			className="tt-scene-device-morph-step absolute inset-0 flex flex-col px-[1.6em] pt-[1.8em] pb-[1.6em]"
		>
			{id === 'household' ? <HouseholdScreen /> : null}
			{id === 'identity' ? <IdentityScreen /> : null}
			{id === 'rental' ? (
				<ChoiceScreen
					title={MORPH_COPY.rental.title}
					sub={MORPH_COPY.rental.sub}
					options={MORPH_COPY.rental.options}
					selected={MORPH_COPY.rental.selected}
				/>
			) : null}
			{id === 'employment' ? (
				<ChoiceScreen
					title={MORPH_COPY.employment.title}
					sub={MORPH_COPY.employment.sub}
					options={MORPH_COPY.employment.options}
					selected={MORPH_COPY.employment.selected}
				/>
			) : null}
			{id === 'bank' ? <BankScreen /> : null}
		</div>
	)
}

function Title({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) {
	return (
		<h4
			className={cn(
				'text-[1.85em] font-semibold leading-[1.1] tracking-[-.02em]',
				className,
			)}
		>
			{children}
		</h4>
	)
}

function Sub({ children }: { children: ReactNode }) {
	return <p className="mt-[.4em] text-[1.1em] text-tt-muted">{children}</p>
}

function Button({ children }: { children: ReactNode }) {
	return (
		<span className="mt-auto self-end rounded-[2em] bg-tt-ink px-[1.4em] py-[.7em] text-[1.1em] font-semibold text-white">
			{children}
		</span>
	)
}

const AVATAR_TONE: Record<AvatarTone, string> = {
	sage: 'bg-tt-sage',
	lav: 'bg-tt-lav',
}

function HouseholdScreen() {
	const copy = MORPH_COPY.household
	return (
		<>
			<Title>{copy.title}</Title>
			<Sub>{copy.sub}</Sub>
			{copy.people.map((person) => (
				<div
					key={person.name}
					className="mt-[.9em] flex items-center gap-[.9em] rounded-[1em] border border-tt-line-soft px-[.9em] py-[.8em]"
				>
					<span
						className={cn(
							'flex h-[2.8em] w-[2.8em] flex-none items-center justify-center rounded-full text-[1em] font-bold',
							AVATAR_TONE[person.tone],
						)}
					>
						{person.initials}
					</span>
					<div>
						<b className="block text-[1.2em] font-semibold">{person.name}</b>
						<small className="block text-[.95em] text-tt-muted">
							{person.role}
						</small>
					</div>
				</div>
			))}
			<div className="mt-[.9em] flex items-center justify-center rounded-[1em] border border-dashed border-tt-line px-[.9em] py-[.8em] text-[1.15em] font-medium text-tt-muted">
				{copy.add}
			</div>
			<Button>{MORPH_COPY.next}</Button>
		</>
	)
}

const ID_LINES = [
	{ top: '24%', width: '40%' },
	{ top: '40%', width: '30%' },
	{ top: '56%', width: '36%' },
] as const

function IdentityScreen() {
	const copy = MORPH_COPY.identity
	return (
		<>
			<Title>{copy.title}</Title>
			<Sub>{copy.sub}</Sub>
			<div className="relative mx-auto mt-[1.4em] aspect-[1.58] w-[80%] overflow-hidden rounded-[1em] border border-tt-line bg-[linear-gradient(135deg,#f7f1fb,#eef4e6)]">
				<i className="absolute top-[18%] left-[8%] block h-[52%] w-[26%] rounded-[.6em] bg-tt-lav" />
				{ID_LINES.map((line) => (
					<i
						key={line.top}
						className="absolute left-[40%] block h-[.7em] rounded-[1em] bg-[rgba(50,48,47,.12)]"
						style={{ top: line.top, width: line.width }}
					/>
				))}
				<i className="tt-scene-device-morph-scan block" />
			</div>
			<div className="mt-[1.2em] flex items-center justify-center gap-[.5em] text-[1em] text-tt-muted">
				{copy.securedBy}
				<img
					src={copy.veriff.src}
					alt={copy.veriff.alt}
					width={434}
					height={119}
					loading="lazy"
					decoding="async"
					className="h-[1.1em] w-auto"
				/>
			</div>
			<Button>{MORPH_COPY.next}</Button>
		</>
	)
}

function ChoiceScreen({
	title,
	sub,
	options,
	selected,
}: {
	title: string
	sub: string
	options: readonly string[]
	selected: number
}) {
	return (
		<>
			<Title>{title}</Title>
			<Sub>{sub}</Sub>
			{options.map((option, index) => {
				const on = index === selected
				return (
					<div
						key={option}
						className={cn(
							'mt-[.8em] flex items-center gap-[.8em] rounded-[1em] border p-[1em] text-[1.2em] font-medium',
							on ? 'border-tt-ink' : 'border-tt-line-soft',
						)}
					>
						{option}
						<i
							className={cn(
								'ml-auto block h-[1.2em] w-[1.2em] flex-none rounded-full',
								on
									? 'border-[.35em] border-tt-ink'
									: 'border-[1.5px] border-tt-line',
							)}
						/>
					</div>
				)
			})}
			<Button>{MORPH_COPY.next}</Button>
		</>
	)
}

function BankScreen() {
	const copy = MORPH_COPY.bank
	return (
		<>
			<div className="flex justify-center">
				<img
					src={copy.flinks.src}
					alt={copy.flinks.alt}
					width={409}
					height={155}
					loading="lazy"
					decoding="async"
					className="h-[1.8em] w-auto"
				/>
			</div>
			<Title className="mt-[.6em]">{copy.title}</Title>
			<div className="mt-[.9em] flex items-center gap-[.7em] rounded-[.8em] border border-tt-line px-[1em] py-[.8em] text-[1.1em] text-tt-muted">
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth={2}
					strokeLinecap="round"
					className="h-[1.2em] w-[1.2em] flex-none"
					aria-hidden="true"
				>
					<circle cx="11" cy="11" r="7" />
					<path d="m20 20-3.5-3.5" />
				</svg>
				{copy.search}
			</div>
			<div className="mt-[.8em] flex items-center gap-[.6em] text-[1em] text-tt-body">
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="#3B9BD8"
					strokeWidth={2}
					strokeLinecap="round"
					strokeLinejoin="round"
					className="h-[1.3em] w-[1.3em] flex-none"
					aria-hidden="true"
				>
					<rect x="5" y="10" width="14" height="11" rx="2.5" />
					<path d="M8 10V7a4 4 0 0 1 8 0v3" />
				</svg>
				{copy.lock}
			</div>
			<div className="tt-scene-device-morph-banks mt-[.8em] grid grid-cols-1 gap-[.55em]">
				{copy.banks.map((bank) => (
					<div
						key={bank.name}
						className="tt-scene-device-morph-bank flex items-center gap-[.8em] rounded-[.9em] bg-white px-[.8em] py-[.6em] shadow-tt-soft"
					>
						<span
							className="flex h-[2.4em] w-[2.4em] flex-none items-center justify-center rounded-full text-[.8em] font-extrabold tracking-[-.02em] text-white"
							style={{ background: bank.colour }}
						>
							{bank.mark}
						</span>
						<div className="min-w-0">
							<b className="block truncate text-[1.1em] font-semibold">
								{bank.name}
							</b>
							<small className="block truncate text-[.85em] text-tt-muted">
								{bank.domain}
							</small>
						</div>
						<span className="ml-auto text-[1.3em] text-tt-body">›</span>
					</div>
				))}
			</div>
			<Button>{MORPH_COPY.submit}</Button>
		</>
	)
}
